import React, { useEffect, useMemo, useState } from "react";
import { faChevronLeft } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styles from "./InstantTournaments.module.scss";
import { ButtonWithSound } from "../../AudioProvider";
import pairImg from "./assets/pair_bg.png";
import pairPersonBg from "./assets/pair_person.png";
import playerPersonBg from "./assets/player-item-photo-bg.png";
import emptyPhoto from "./assets/empty_photo.png";
import randomPairActive from "./assets/random-pair.png";
import randomPair from "./assets/random-pair-not-active.png";
import pairCombine from "./assets/pair-combine.png";
import trashIcon from "./assets/trash.png";
import waitingIcon from "./assets/waiting-icon.png";
import invitationDeclinedIcon from "./assets/invitation-declined.png";
import successIcon from "./assets/accepted.png";
import classNames from "classnames";
import { TournamentChat } from "../../Chat/TournamentChat";
import { loader } from "graphql.macro";
import { useQuery } from "../../../hooks/useQuery";
import {
  ScheduledTournamentQuery,
  ScheduledTournamentQueryVariables,
} from "./graphql/CurrentScheduledTournamentQuery.generated";
import { AbsoluteCenter } from "../../AbsoluteCenter";
import { Loader } from "../../Loader";
import { ScheduledTournamentPlayerType } from "./types";
import {
  ParticipantRequestStatus,
  TournamentPlayerPerTeam,
} from "../../../graphql/generated";
import { useMutation } from "../../../hooks/useMutation";
import {
  CreateScheduledTournamentPairRequestMutation,
  CreateScheduledTournamentPairRequestMutationVariables,
} from "./graphql/CreateScheduledTournamentPairRequestMutation.generated";
import {
  UnlinkScheduledTournamentPartnerMutation,
  UnlinkScheduledTournamentPartnerMutationVariables,
} from "./graphql/UnlinkScheduledTournamentPartner.generated";
import {
  PairMeRandomlyInScheduledTournamentMutation,
  PairMeRandomlyInScheduledTournamentMutationVariables,
} from "./graphql/PairMeRandomlyInScheduledTournament.generated";
import { ProfileImage } from "../../ProfileImage";
import { useTranslation } from "react-i18next";
import { ScheduledTournamentInvitePlayers } from "./ScheduledTournamentInvitePlayers";
import * as Types from "../../../graphql/generated";
import { useSubscription } from "../../../hooks/useSubscription";
import {
  ScheduledTournamentSubscription,
  ScheduledTournamentSubscriptionVariables,
} from "./graphql/ScheduledTournamentSubscription.generated";
import { useUser, useUserId } from "../../UserProvider";
import {
  ScheduledTournamentParticipantRequestsSubscription,
  ScheduledTournamentParticipantRequestsSubscriptionVariables,
} from "../../ScheduledTournamentEvents/graphql/ScheduledTournamentEventsSubscription.generated";
import roundTournamentImg from "./assets/round-tournament-bg.png";
import { SelectPairErrorModal } from "./SelectPairErrorModal";
import { FewInvitePairErrorModal } from "./FewInvitePairErrorModal";
import { RemovedFromPairErrorModal } from "./RemovedFromPairErrorModal";
import { RandomInvitePairErrorModal } from "./RandomInvitePairErrorModal";
import dayjs from "dayjs";
import * as Sentry from "@sentry/browser";
import { ChatMessage } from "../../Chat/InlineChat/types";

const SCHEDULED_TOURNAMENT_QUERY = loader(
  "./graphql/CurrentScheduledTournamentQuery.graphql"
);

const CREATE_PAIR_QUERY = loader(
  "./graphql/CreateScheduledTournamentPairRequestMutation.graphql"
);

const REMOVE_PAIR_QUERY = loader(
  "./graphql/UnlinkScheduledTournamentPartner.graphql"
);

const PAIR_ME_RANDOMLY_QUERY = loader(
  "./graphql/PairMeRandomlyInScheduledTournament.graphql"
);

const SCHEDULED_TOURNAMENT_SUBSCRIPTION = loader(
  "./graphql/ScheduledTournamentSubscription.graphql"
);

const SCHEDULED_TOURNAMENT_EVENTS_SUBSCRIPTION = loader(
  "../../ScheduledTournamentEvents/graphql/ScheduledTournamentEventsSubscription.graphql"
);

const MY_PAIR_AVATAR_SIZE = {
  width: 46,
  height: 46,
};

const PLAYER_ITEM_AVATAR_SIZE = {
  width: 38,
  height: 38,
};

type TournamentInfoType = {
  numberOfPlayers: number;
  joinedPlayers: number;
  pairsInfo: {
    numberOfPairs: number;
    createdPairs: number;
  };
  disableRandom: boolean;
};

const validInvitationTime =
  process.env.REACT_APP_VALID_TOURNAMENT_INVITATION_TIME || 30;

const MyPairTitle = ({
  myPair,
  onPairDelete,
}: {
  myPair: ScheduledTournamentPlayerType[];
  onPairDelete: (id: string) => void;
}) => {
  const { t } = useTranslation();
  const myName = myPair[0]?.player?.name || "";
  const partnerName =
    myPair[1]?.player?.name || t("instant_tournament.your_partner");

  const [, removePair] = useMutation<
    UnlinkScheduledTournamentPartnerMutation,
    UnlinkScheduledTournamentPartnerMutationVariables
  >(REMOVE_PAIR_QUERY, false);

  const removePartner = () => {
    removePair({ scheduledTournamentId: myPair[0].scheduledTournamentId }).then(
      () => {
        onPairDelete(myPair[1].player.id);
      }
    );
  };

  return (
    <div
      className="flex items-center justify-between mx-6"
      style={{
        background: `url(${pairImg}) center center no-repeat`,
        backgroundSize: "contain",
        height: "3.5rem",
      }}
    >
      <div
        className="flex items-center gap-4"
        style={{ transform: "translateX(-12px)" }}
      >
        <div
          className={classNames(
            "flex items-center justify-center",
            styles.pairPhoto
          )}
          style={{ background: `url(${pairPersonBg}) center center no-repeat` }}
        >
          <ProfileImage
            className="bg-gray-400 border rounded-full"
            id={myPair[0]?.player?.id || ""}
            name={myPair[0]?.player?.name || ""}
            {...MY_PAIR_AVATAR_SIZE}
          />
        </div>
        <span className="text-base text-yellow-400 font-bold">{myName}</span>
      </div>
      <div
        className="flex items-center gap-4"
        style={{ transform: "translateX(12px)" }}
      >
        {myPair[1]?.player && (
          <button
            className="btn flex items-center justify-center btn-blue-900 w-6 h-6 p-0"
            style={{
              borderRadius: "50%",
              boxShadow: "0 1px 2px 0 rgba(136, 136, 136, 0.25) inset",
            }}
            onClick={removePartner}
          >
            <img width={16} height={16} src={trashIcon} alt="icon" />
          </button>
        )}
        <span className="text-base text-yellow-400 font-bold">
          {partnerName}
        </span>
        <div
          className={classNames(
            "flex items-center justify-center",
            styles.pairPhoto
          )}
          style={{ background: `url(${pairPersonBg}) center center no-repeat` }}
        >
          {myPair[1] ? (
            <ProfileImage
              className="bg-gray-400 border rounded-full"
              id={myPair[1]?.player?.id || ""}
              name={myPair[1]?.player?.name || ""}
              {...MY_PAIR_AVATAR_SIZE}
            />
          ) : (
            <img src={emptyPhoto} alt="img" />
          )}
        </div>
      </div>
    </div>
  );
};

const TournamentInfo = ({
  numberOfPlayers,
  joinedPlayers,
  pairsInfo,
  tournamentId,
  disableRandom,
}: {
  numberOfPlayers: number;
  joinedPlayers: number;
  pairsInfo: { numberOfPairs: number; createdPairs: number };
  tournamentId: string;
  disableRandom: boolean;
}) => {
  const { user } = useUser();
  const { t } = useTranslation();
  const [active, setActive] = React.useState(false);

  const [, pairMeRandomly] = useMutation<
    PairMeRandomlyInScheduledTournamentMutation,
    PairMeRandomlyInScheduledTournamentMutationVariables
  >(PAIR_ME_RANDOMLY_QUERY, false);

  const handlePairMeRandomly = () => {
    pairMeRandomly({ scheduledTournamentId: tournamentId }).then((data) => {
      if (data?.error) {
        Sentry.captureMessage(
          `Error on pair randomly to scheduled tournament: scheduledTournamentId: ${tournamentId}, userId: ${user}`
        );
      }
    });
  };

  return (
    <div className="flex items-center justify-between px-3">
      <div className="flex items-center gap-5">
        <span className="text-white text-base">
          {t("instant_tournament.choose_your_partner")}
        </span>
        <button
          onMouseEnter={() => setActive(true)}
          onMouseLeave={() => setActive(false)}
          className={classNames(
            "btn flex items-center rounded-md px-2 py-1 gap-2",
            { "text-orange-400": active, "text-gray-600": !active }
          )}
          style={{
            background: "linear-gradient(180deg, #222D3B 0%, #222D3B 100%)",
          }}
          onClick={handlePairMeRandomly}
          disabled={disableRandom}
        >
          <img
            style={{ width: "1.5rem", height: "1.5rem" }}
            src={active ? randomPairActive : randomPair}
            alt="icon"
          />
          <span>{t("instant_tournament.pair_me_randomly")}</span>
        </button>
      </div>
      <div className="flex items-center justify-end gap-5 text-sm">
        <div className="flex items-center gap-1">
          <span className="text-gray-600">{t("players")}</span>
          <span className="text-white">{`${joinedPlayers}/${numberOfPlayers}`}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-gray-600">
            {t("instant_tournament.pairs_formed")}
          </span>
          <span className="text-white">{`${pairsInfo.createdPairs}/${pairsInfo.numberOfPairs}`}</span>
        </div>
      </div>
    </div>
  );
};

const PlayerItem = ({
  player,
  isTwoVSTwo,
  isUserPaired,
  onSetInviteError,
}: {
  player: ScheduledTournamentPlayerType;
  isTwoVSTwo?: boolean;
  isUserPaired?: boolean;
  onSetInviteError?: () => void;
}) => {
  const { t } = useTranslation();
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showRandomErrorModal, setShowRandomErrorModal] = useState(false);

  const [, createPair] = useMutation<
    CreateScheduledTournamentPairRequestMutation,
    CreateScheduledTournamentPairRequestMutationVariables
  >(CREATE_PAIR_QUERY, false);

  const getCurrentButtonText = (text: ParticipantRequestStatus) => {
    switch (text) {
      case ParticipantRequestStatus.Free:
        return t("instant_tournament.select_pair");
      case ParticipantRequestStatus.Accepted:
        return "";
      case ParticipantRequestStatus.Pending:
        return t("instant_tournament.pending");
      case ParticipantRequestStatus.Declined:
        return t("instant_tournament.declined");
      case ParticipantRequestStatus.InPair:
        return t("instant_tournament.in_a_pair");
    }
  };

  const addPair = () => {
    createPair({
      input: {
        scheduledTournamentId: player.scheduledTournamentId,
        pairUserId: player.player.id,
      },
    }).then((data) => {
      if (
        data?.error?.message?.includes(
          "Cannot create pair request when user has requested random pairing"
        )
      ) {
        setShowRandomErrorModal(true);
      } else if (
        data?.error?.message?.includes(
          "Previous pair request has not expired yet"
        )
      ) {
        onSetInviteError && onSetInviteError();
      }
    });
  };

  const showModal = () => {
    setShowInfoModal(true);
  };

  return (
    <div
      className="flex items-center py-2 px-4 justify-between rounded"
      style={{
        background: "#222D3B",
        borderLeft: "2px solid rgba(228, 160, 54)",
      }}
    >
      <div className="flex items-center gap-4">
        <div
          className={classNames(
            "flex items-center justify-center",
            styles.playerPhoto
          )}
          style={{
            background: `url(${playerPersonBg}) center center no-repeat`,
          }}
        >
          <ProfileImage
            className="bg-gray-400 border rounded-full"
            id={player.player.id}
            name={player.player.name}
            {...PLAYER_ITEM_AVATAR_SIZE}
          />
        </div>
        <div className="flex flex-col">
          <span className="text-white">{player.player.name}</span>
        </div>
      </div>
      {isTwoVSTwo && (
        <div className="flex items-center gap-4">
          {player.requestStatus === ParticipantRequestStatus.Declined && (
            <div
              className="flex items-center gap-2 text-sm"
              style={{ color: "#FF7C7C" }}
            >
              <img src={invitationDeclinedIcon} alt="icon" />
              <span>{t("instant_tournament.invitation_declined")}</span>
            </div>
          )}
          <button
            className={classNames(
              "btn flex items-center rounded-md px-6 py-1 gap-2 text-white",
              {
                "btn-orange-600":
                  player.requestStatus === ParticipantRequestStatus.Free,
                "btn-blue-800":
                  player.requestStatus === ParticipantRequestStatus.Pending,
                "btn-blue-900":
                  player.requestStatus === ParticipantRequestStatus.InPair,
                "btn-red-900":
                  player.requestStatus === ParticipantRequestStatus.Declined,
              }
            )}
            disabled={
              player.requestStatus === ParticipantRequestStatus.Pending ||
              player.requestStatus === ParticipantRequestStatus.InPair ||
              player.requestStatus === ParticipantRequestStatus.Declined ||
              player.doPairRandomly
            }
            style={{
              opacity: player.doPairRandomly ? 0.8 : 1,
            }}
            onClick={isUserPaired ? showModal : addPair}
          >
            <span style={{ transform: "translateY(-1px)" }}>
              {getCurrentButtonText(player.requestStatus)}
            </span>
          </button>
        </div>
      )}
      {showInfoModal && (
        <SelectPairErrorModal
          onClose={() => {
            setShowInfoModal(false);
          }}
        />
      )}

      {showRandomErrorModal && (
        <RandomInvitePairErrorModal
          onClose={() => {
            setShowRandomErrorModal(false);
          }}
        />
      )}
    </div>
  );
};

const Pair = ({ players }: { players: ScheduledTournamentPlayerType[] }) => {
  return (
    <div className="flex flex-col gap-1 relative">
      <img
        src={pairCombine}
        alt="img"
        className="absolute"
        style={{
          transform: "translate(-80%, 36%)",
        }}
      />
      <PlayerItem player={players[0]} isTwoVSTwo />
      <PlayerItem player={players[1]} isTwoVSTwo />
    </div>
  );
};

const PlayersList = ({
  players,
  isTwoVSTwo,
  isUserPaired,
  onSetInviteError,
}: {
  players: ScheduledTournamentPlayerType[][];
  isTwoVSTwo: boolean;
  isUserPaired?: boolean;
  onSetInviteError: () => void;
}) => {
  const filteredPlayers = useMemo(() => {
    return isTwoVSTwo ? players?.slice(1) || [] : players;
  }, [players]);

  return (
    <div
      className={classNames(
        "flex flex-col gap-1 px-3 overflow-auto",
        styles.scrollable
      )}
      style={{ height: `calc(100% - ${isTwoVSTwo ? "6.25rem" : "0px"})` }}
    >
      {!isTwoVSTwo &&
        filteredPlayers?.map((item: ScheduledTournamentPlayerType[]) => {
          return <PlayerItem player={item[0]} />;
        })}
      {isTwoVSTwo &&
        filteredPlayers?.map((item: ScheduledTournamentPlayerType[]) => {
          return item.length === 1 ? (
            <PlayerItem
              key={item[0].player.id}
              player={item[0]}
              isTwoVSTwo={isTwoVSTwo}
              isUserPaired={isUserPaired}
              onSetInviteError={onSetInviteError}
            />
          ) : (
            <Pair players={item} />
          );
        })}
    </div>
  );
};

export const ScheduledTournamentBoard = ({
  onClose,
  roomId,
  tournamentId,
}: {
  onClose: () => void;
  roomId: string;
  tournamentId: string;
}) => {
  const { t } = useTranslation();
  const id = useUserId();
  const [showInviteFriends, setShowInviteFriends] = useState(false);
  const [userToDeleteFromPair, setUserToDeleteFromPair] = useState<
    string | null
  >(null);
  const [showRemovedFromPairModal, setShowRemovedFromPairModal] = useState(
    false
  );
  const [showInvitePairErrorModal, setShowInvitePairErrorModal] = useState("");
  const [isInviteError, setIsInviteError] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [tournamentInfo, setTournamentInfo] = useState<TournamentInfoType>({
    numberOfPlayers: 0,
    joinedPlayers: 0,
    pairsInfo: {
      numberOfPairs: 0,
      createdPairs: 0,
    },
    disableRandom: false,
  });
  const [participants, setParticipants] = useState<
    ScheduledTournamentPlayerType[][]
  >([]);

  const [{ data: tournamentData, fetching }] = useQuery<
    ScheduledTournamentQuery,
    ScheduledTournamentQueryVariables
  >({
    query: SCHEDULED_TOURNAMENT_QUERY,
    variables: {
      id: tournamentId,
    },
  });

  useEffect(() => {
    if (!showToast) return;

    const timer = setTimeout(() => {
      setShowToast(false);
    }, 3200);

    return () => clearTimeout(timer);
  }, [showToast]);

  const [{ data: subscriptionData }] = useSubscription<
    unknown,
    ScheduledTournamentSubscription,
    ScheduledTournamentSubscriptionVariables
  >({
    query: SCHEDULED_TOURNAMENT_SUBSCRIPTION,
    variables: {
      id: tournamentId,
    },
    throwOnError: false,
  });

  const [{ data: requestPairData }] = useSubscription<
    unknown,
    ScheduledTournamentParticipantRequestsSubscription,
    ScheduledTournamentParticipantRequestsSubscriptionVariables
  >({
    query: SCHEDULED_TOURNAMENT_EVENTS_SUBSCRIPTION,
    throwOnError: false,
  });

  const chatData = useMemo(() => {
    if (
      subscriptionData?.scheduledTournament?.__typename ===
      "ScheduledTournamentChatMessages"
    ) {
      return subscriptionData.scheduledTournament;
    }
    return null;
  }, [subscriptionData]);

  useEffect(() => {
    if (tournamentData) {
      setTournamentInfo({
        numberOfPlayers: tournamentData.scheduledTournament?.numberOfPlayers!,
        joinedPlayers: tournamentData.scheduledTournament?.joinedPlayers!,
        pairsInfo: {
          numberOfPairs: tournamentData.scheduledTournament?.pairsInfo
            ?.numberOfPairs!,
          createdPairs: tournamentData.scheduledTournament?.pairsInfo
            ?.createdPairs!,
        },
        disableRandom:
          (tournamentData.scheduledTournament?.participants?.[0]?.length ?? 0) >
          1,
      });

      setParticipants([
        ...(tournamentData.scheduledTournament
          ?.participants! as ScheduledTournamentPlayerType[][]),
      ]);
    }
  }, [tournamentData?.scheduledTournament?.id]);

  useEffect(() => {
    if (
      requestPairData?.scheduledTournamentParticipantRequests?.fromMe
        ?.requestStatus
    ) {
      const data =
        requestPairData?.scheduledTournamentParticipantRequests?.fromMe;

      const updated = participants.map((group) =>
        group.map((participant) => {
          if (participant.player.id === data.toUserId) {
            return {
              ...participant,
              requestStatus: data?.requestStatus,
            };
          }
          return participant;
        })
      );

      setParticipants(updated);
    }
  }, [requestPairData]);

  useEffect(() => {
    if (subscriptionData) {
      if (
        subscriptionData.scheduledTournament?.__typename ===
        "ScheduledTournamentEntityUpdate"
      ) {
        setTournamentInfo({
          numberOfPlayers: subscriptionData?.scheduledTournament?.tournament
            ?.numberOfPlayers!,
          joinedPlayers: subscriptionData?.scheduledTournament?.tournament
            ?.joinedPlayers!,
          pairsInfo: {
            numberOfPairs: subscriptionData?.scheduledTournament?.tournament
              ?.pairsInfo?.numberOfPairs!,
            createdPairs: subscriptionData?.scheduledTournament?.tournament
              ?.pairsInfo?.createdPairs!,
          },
          disableRandom: false,
        });
      } else if (
        subscriptionData.scheduledTournament?.__typename ===
        "ScheduledTournamentParticipantUpdate"
      ) {
        if (
          Array.isArray(participants?.[0]) &&
          participants[0].length === 2 &&
          id ===
            subscriptionData.scheduledTournament?.participant?.player?.id &&
          subscriptionData.scheduledTournament.operation === "UPDATE" &&
          subscriptionData.scheduledTournament.participant?.requestStatus ===
            "FREE" &&
          !userToDeleteFromPair
        ) {
          setShowRemovedFromPairModal(true);
        }

        if (
          id ===
            subscriptionData.scheduledTournament?.participant?.player?.id &&
          subscriptionData.scheduledTournament.participant?.requestTime &&
          isInviteError
        ) {
          const isRequestPeriodValid = dayjs(
            subscriptionData.scheduledTournament.participant?.requestTime
          )
            .add(+validInvitationTime, "second")
            .isAfter(dayjs());

          if (isRequestPeriodValid) {
            setShowInvitePairErrorModal(
              subscriptionData.scheduledTournament.participant?.requestTime
            );
          }
        }

        const subscriptionParticipant =
          subscriptionData.scheduledTournament?.participant;

        if (subscriptionData.scheduledTournament?.participant.pairUserId) {
          const foundPairIndex = participants.findIndex((group) =>
            group.some(
              (participant) =>
                participant.player.id === subscriptionParticipant.pairUserId
            )
          );

          const foundDeletePlayerIndex = participants.findIndex((group) =>
            group.some(
              (participant) =>
                participant.player.id === subscriptionParticipant?.player.id
            )
          );

          const updatedData = participants.map((group, index) => {
            if (index === foundPairIndex) {
              if (group.length === 2) return group;

              return group[0].player.id === id
                ? [
                    {
                      ...group[0],
                      requestStatus: ParticipantRequestStatus.InPair,
                    },
                    subscriptionParticipant,
                  ]
                : [
                    subscriptionParticipant,
                    {
                      ...group[0],
                      requestStatus: ParticipantRequestStatus.InPair,
                    },
                  ];
            } else if (index === foundDeletePlayerIndex) {
              return [];
            } else {
              return group;
            }
          });

          if (
            foundPairIndex !== 0 &&
            subscriptionParticipant.player.id === id
          ) {
            const newArr = [...updatedData];
            const [element] = newArr.splice(foundPairIndex, 1);
            newArr.splice(0, 0, element);

            const cleaned = newArr.filter((group) => group?.length > 0);
            setParticipants(cleaned as ScheduledTournamentPlayerType[][]);
          } else {
            const cleaned = updatedData.filter((group) => group.length > 0);
            setParticipants(cleaned as ScheduledTournamentPlayerType[][]);
          }

          if (
            subscriptionData.scheduledTournament?.participant.pairUserId === id
          ) {
            const isMyPairStatus =
              subscriptionData.scheduledTournament?.participant
                .requestStatus === ParticipantRequestStatus.Accepted ||
              subscriptionData.scheduledTournament?.participant
                .requestStatus === ParticipantRequestStatus.InPair;

            setTournamentInfo({
              ...tournamentInfo,
              disableRandom: isMyPairStatus,
            });
          }
        } else {
          const foundPairIndex = participants.findIndex((group) =>
            group.some(
              (participant) =>
                participant.player.id === subscriptionParticipant?.player.id
            )
          );
          let found = false;

          const updatedData = participants.map((innerArr, index) => {
            if (index === 0) {
              if (subscriptionParticipant?.player.id === id) {
                found = true;
                return [subscriptionParticipant];
              } else {
                return innerArr;
              }
            } else {
              if (index === foundPairIndex) {
                found = true;
                return [subscriptionParticipant];
              } else {
                return innerArr;
              }
            }
          });

          if (!found) {
            updatedData.push([subscriptionParticipant]);
          }

          setParticipants(updatedData as ScheduledTournamentPlayerType[][]);
        }
      }
    }
  }, [subscriptionData, userToDeleteFromPair, isInviteError]);

  const isTwoVSTwo = useMemo(() => {
    return (
      tournamentData?.scheduledTournament?.playerPerTeam ===
      Types.TournamentPlayerPerTeam.TwoVsTwo
    );
  }, [tournamentData]);

  if (!tournamentData && fetching) {
    return (
      <AbsoluteCenter>
        <Loader description="Fetching tournament" />
      </AbsoluteCenter>
    );
  }

  const handlePairDelete = (id: string) => {
    setUserToDeleteFromPair(id);
  };

  //@ts-ignore
  return (
    <div className="h-full flex flex-col gap-3">
      <div>
        <button
          className="flex items-center text-gray-500 gap-2"
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
          <span>{t("instant_tournament.back_to_my_tournaments")}</span>
        </button>
      </div>
      <div className="flex flex-col gap-3 mt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className={styles.roundImage}
              style={{
                backgroundImage: `url(${roundTournamentImg})`,
                backgroundSize: "contain",
              }}
            >
              <img
                src={`${process.env.REACT_APP_API_HOST}/scheduled-tournaments/${tournamentData?.scheduledTournament?.image}`}
                alt="img"
              />
            </div>
            <span className="text-white text-2xl font-semibold">
              {tournamentData?.scheduledTournament?.name}
            </span>
          </div>
          <ButtonWithSound
            className="btn btn-blue-600 btn-sm text-sm rounded-md pt-1 pb-2 px-6"
            onClick={() => setShowInviteFriends(true)}
            disabled={participants[0]?.length === 2}
          >
            {t("instant_tournament.invite_friend")}
          </ButtonWithSound>
        </div>
      </div>
      <div className="flex gap-3" style={{ height: "calc(100% - 7.5rem)" }}>
        <div className="flex flex-col w-2/3 gap-3 h-full rounded-lg bg-gray-900 py-3">
          {isTwoVSTwo && (
            <>
              {participants[0] && (
                <MyPairTitle
                  myPair={participants[0] as ScheduledTournamentPlayerType[]}
                  onPairDelete={handlePairDelete}
                />
              )}
              {participants[0]?.length === 2 && (
                <div className="flex items-center justify-center gap-2">
                  <img
                    src={waitingIcon}
                    alt="icon"
                    style={{ width: "1.5rem", height: "1.5rem" }}
                  />
                  <span className={styles.info}>
                    {t("instant_tournament.waiting_for_players")}
                  </span>
                </div>
              )}
              {participants?.[0]?.[0]?.doPairRandomly && (
                <div className="flex items-center justify-center gap-2">
                  <img
                    src={waitingIcon}
                    alt="icon"
                    style={{ width: "1.5rem", height: "1.5rem" }}
                  />
                  <span className={styles.info}>
                    {t("instant_tournament.randomly_paired_info")}
                  </span>
                </div>
              )}
              <TournamentInfo
                joinedPlayers={tournamentInfo?.joinedPlayers || 0}
                numberOfPlayers={tournamentInfo?.numberOfPlayers!}
                pairsInfo={tournamentInfo?.pairsInfo!}
                tournamentId={tournamentData?.scheduledTournament?.id!}
                disableRandom={
                  tournamentInfo?.disableRandom ||
                  participants?.[0]?.[0]?.doPairRandomly
                }
              />
            </>
          )}
          <PlayersList
            players={participants}
            isTwoVSTwo={isTwoVSTwo}
            isUserPaired={participants[0]?.length === 2}
            onSetInviteError={() => setIsInviteError(true)}
          />
        </div>
        <div className="w-1/3">
          <TournamentChat
            id={tournamentData?.scheduledTournament?.id!}
            type="scheduled-tournament"
            //@ts-ignore
            updatedMessages={chatData?.messages as ChatMessage}
          />
        </div>
      </div>
      {showInviteFriends && (
        <ScheduledTournamentInvitePlayers
          tournamentId={tournamentData?.scheduledTournament?.id!}
          onClose={() => setShowInviteFriends(false)}
          onShowInviteMessage={() => {
            setShowToast(true);
          }}
        />
      )}
      {showRemovedFromPairModal && !userToDeleteFromPair && (
        <RemovedFromPairErrorModal
          onClose={() => {
            setUserToDeleteFromPair(null);
            setShowRemovedFromPairModal(false);
          }}
        />
      )}
      {!!showInvitePairErrorModal && (
        <FewInvitePairErrorModal
          onClose={() => {
            setShowInvitePairErrorModal("");
            setIsInviteError(false);
          }}
          requestTime={showInvitePairErrorModal}
        />
      )}
      {showToast && (
        <div className={styles.toast}>
          <img src={successIcon} alt="" />
          <span>{t("instant_tournament.friend_invited")}</span>
        </div>
      )}
    </div>
  );
};
